import Logger from 'src/util/Logger';
import { QUESTION_TYPES } from './constants';

/**
 * Format response date consistently
 * @param {string|number} timestamp - Timestamp to format
 * @returns {string} Formatted date string or 'Unknown' if invalid
 */
export const formatResponseDate = (timestamp) => {
  try {
    // Convert to milliseconds if timestamp is in seconds
    let timestampMs = timestamp;
    if (typeof timestamp === 'number' && timestamp < 1e12) {
      timestampMs = timestamp * 1000;
    }

    if (!timestampMs) {
      return 'Unknown';
    }
    
    const date = new Date(timestampMs);
    if (isNaN(date.getTime())) {
      return 'Unknown';
    }
    
    return date.toISOString();
  } catch (error) {
    Logger.error('Error formatting date:', {
      error,
      timestamp
    });
    return 'Unknown';
  }
};

/**
 * Extract clean event ID from various formats
 * @param {string} eventId - Raw event ID that might contain URL or path
 * @returns {string} Clean event ID
 */
export const extractEventId = (eventId) => {
  if (!eventId) return '';
  
  try {
    if (!eventId.includes('/')) return eventId;
    
    // Handle full URLs
    if (eventId.includes('://')) {
      const url = new URL(eventId);
      const pathParts = url.pathname.split('/').filter(part => part.length > 0);
      
      for (let i = 0; i < pathParts.length; i++) {
        if ((pathParts[i] === 'q' || pathParts[i] === 'r') && i + 1 < pathParts.length) {
          return pathParts[i + 1];
        }
      }
    }
    
    // Handle path-only format
    const parts = eventId.split('/').filter(part => part.length > 0);
    for (let i = 0; i < parts.length; i++) {
      if ((parts[i] === 'q' || parts[i] === 'r') && i + 1 < parts.length) {
        return parts[i + 1];
      }
    }
    
    return eventId;
  } catch (error) {
    Logger.error('Error extracting event ID:', error);
    return eventId;
  }
};

/**
 * Normalizes a question type to its standard format
 * @param {string} questionType - The question type to normalize
 * @returns {string} The normalized question type
 */
export function normalizeQuestionType(questionType) {
  if (!questionType) {
    console.warn('No question type provided, defaulting to default');
    return 'default';
  }

  // Convert to lowercase for comparison with EventQuestionType values
  const type = questionType.toLowerCase();

  // Map common variations to standard types
  const typeMap = {
    // Standard types (direct mappings)
    'default': 'default',
    'binary': 'binary',
    'multiple': 'multiple',
    'multiple_rating': 'multiple_rating',
    'scale': 'scale',
    'scale_5': 'scale_5',
    'scale_10': 'scale_10',
    'stack_rank': 'stack_rank',

    // Common variations for default/open-ended
    'open': 'default',
    'open_ended': 'default',
    'openended': 'default',
    'open-ended': 'default',
    'text': 'default',
    'free_text': 'default',
    'freetext': 'default',
    'free-text': 'default',

    // Common variations for binary
    'yes_no': 'binary',
    'yesno': 'binary',
    'yes-no': 'binary',
    'true_false': 'binary',
    'truefalse': 'binary',
    'true-false': 'binary',

    // Common variations for multiple choice
    'choice': 'multiple',
    'multiplechoice': 'multiple',
    'multiple-choice': 'multiple',
    'multiple_choice': 'multiple',

    // Common variations for scale
    'rating_scale': 'scale',
    'ratingscale': 'scale',
    'rating-scale': 'scale',
    'scale_rating': 'scale',
    'scalerating': 'scale',
    'scale-rating': 'scale',

    // Common variations for scale_5
    'five_scale': 'scale_5',
    'five-scale': 'scale_5',
    'scale_five': 'scale_5',
    'scale-five': 'scale_5',

    // Common variations for scale_10
    'ten_scale': 'scale_10',
    'ten-scale': 'scale_10',
    'scale_ten': 'scale_10',
    'scale-ten': 'scale_10',

    // Common variations for stack_rank
    'stack': 'stack_rank',
    'stackrank': 'stack_rank',
    'stack-rank': 'stack_rank'
  };

  // Get normalized type from map or use original if not found
  const normalizedType = typeMap[type] || type;

  // Check if normalized type is supported
  const supportedTypes = [
    'default',      // OPEN_ENDED
    'binary',
    'multiple',     // MULTIPLE_CHOICE
    'multiple_rating',
    'scale',
    'scale_5',
    'scale_10',
    'stack_rank'
  ];

  if (!supportedTypes.includes(normalizedType)) {
    console.warn('Unsupported question type detected:', {
      original: questionType,
      normalized: normalizedType,
      supportedTypes
    });
    return 'default'; // Default to OPEN_ENDED for unsupported types
  }

  // console.log('Question type normalized:', {
  //   original: questionType,
  //   normalized: normalizedType,
  //   timestamp: new Date().toISOString()
  // });

  return normalizedType;
}
